import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4pb94pmma3c958r7su0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="bg-amber-50 py-28">
      <div className="max-w-4xl mx-auto px-12">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <p className="text-lg text-gray-600">Have questions? We're here to help</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 mb-16" data-readdy-form>
          <div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your Name"
              className="w-full h-14 px-5 bg-white border border-gray-300 rounded-2xl text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
              required
            />
          </div>

          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Your Email"
              className="w-full h-14 px-5 bg-white border border-gray-300 rounded-2xl text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
              required
            />
          </div>

          <div>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Subject"
              className="w-full h-14 px-5 bg-white border border-gray-300 rounded-2xl text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
              required
            />
          </div>

          <div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Your Message (max 500 characters)"
              maxLength={500}
              rows={6}
              className="w-full px-5 py-4 bg-white border border-gray-300 rounded-2xl text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
              required
            />
            <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 bg-black text-white rounded-2xl text-base font-semibold hover:bg-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-5 py-4 rounded-2xl text-sm">
              Thank you! Your message has been sent successfully.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-2xl text-sm">
              Please fill in all fields correctly.
            </div>
          )}
        </form>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-mail-line text-2xl text-cyan-500"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
              <a href="mailto:contact@construtoramanuellaltda.com" className="text-sm text-gray-600 hover:text-cyan-500 transition-colors">
                contact@construtoramanuellaltda.com
              </a>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-2xl text-cyan-500"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
              <a href="tel:+19787978675" className="text-sm text-gray-600 hover:text-cyan-500 transition-colors">
                +1 9787978675
              </a>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-line text-2xl text-cyan-500"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
              <p className="text-sm text-gray-600">
                Av. EPAMINONDAS JACOME 1166<br />
                RIO BRANCO, Brazil<br />
                69905-232
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
